/**
 * <StudentAssessmentsPage> — « Mes contrôles », la page de l'élève (page).
 *
 * Ce que l'écran d'aujourd'hui montre (`SchoolAssessmentsScreen.js`) : les contrôles À VENIR,
 * rangés par date, et « Ajouter un contrôle ». Ni contrôle passé, ni note : le backend n'en sert
 * pas.
 *
 * La forme est celle que le DS a déjà pour un contrôle : UNE carte-liste, une ligne par contrôle
 * (`<SchoolAssessmentRow>`), qui ouvre la feuille de détail (`<SchoolAssessmentDetailModal>`) — à
 * la place des cartes dépliables. Le mot de l'état de préparation (« Non commencé », « En
 * préparation », « Prêt ») que la carte portait prend le libellé de tête de la ligne.
 *
 * La préparation se lit, elle ne se coche pas : elle monte avec les activités faites sur les
 * chapitres du contrôle.
 *
 * Contrôlée de bout en bout — la feuille ouverte, la confirmation de suppression, la suppression en
 * cours, son échec et la feuille d'ajout arrivent en props : chaque état s'ouvre seul dans une
 * story.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { CardList, CardListLinkRow } from "../../molecules/CardList"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { EmptyState } from "../../molecules/EmptyState"
import { Row } from "../../atoms/containers/Row"
import { ClosableHeader } from "../../molecules/ClosableHeader"
import { RefreshButton } from "../../molecules/RefreshButton"
import { SchoolAssessmentRow } from "../../molecules/SchoolAssessmentRow"
import { Snackbar } from "../../molecules/Snackbar"
import { AddSchoolAssessmentModal, type AddSchoolAssessmentModalProps } from "../../templates/AddSchoolAssessmentModal"
import { PageTemplate } from "../../templates/PageTemplate"
import { SchoolAssessmentDetailModal } from "../../templates/SchoolAssessmentDetailModal"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"
import { preparationProgress } from "../../../ports/primary/ui/formatters/schoolAssessmentFormatter"
import { StudentAssessmentsSkeleton } from "./StudentAssessmentsSkeleton"

type AssessmentId = UISchoolAssessment["id"]

/** Les mots de l'écran d'aujourd'hui, à la 2ᵉ personne. */
const TEXTS = {
    title: "Mes contrôles",
    add: "Ajouter un contrôle",
    emptyTitle: "Aucun contrôle à venir",
    emptyMessage: "Ajoute tes prochains contrôles pour suivre ta préparation",
    detailTitle: "Détails du contrôle",
    deleteTitle: "Supprimer ce contrôle ?",
    deleteMessage: "Cette action est irréversible.",
    deleteConfirm: "Supprimer",
    deleting: "Suppression...",
    formAddTitle: "Ajouter un contrôle",
    formEditTitle: "Modifier le contrôle",
    formAddSubmit: "C'est parti !",
    formEditSubmit: "Enregistrer",
} as const

/**
 * Le mot de la préparation, tel que la carte d'aujourd'hui le dit : rien de fait, tout fait, ou en
 * route. À remonter dans `schoolAssessmentFormatter` au câblage (`assessmentState` y suit d'autres
 * seuils, et personne ne l'appelle).
 */
function preparationStatus(progress: number): string {
    if (progress <= 0) return "Non commencé"
    if (progress >= 100) return "Prêt"
    return "En préparation"
}

/**
 * La feuille d'ajout / de modification, telle que l'écran la pilote. Les mots de l'élève (titre,
 * bouton final, aides) ne sont pas à lui : la page les pose d'après `isEditing`.
 */
export type StudentAssessmentFormProps = Omit<
    AddSchoolAssessmentModalProps,
    "voice" | "title" | "submitLabel" | "submitIconName" | "showSubmitHint"
>

export type StudentAssessmentsPageProps = {
    /** Les contrôles à venir, par date croissante — l'ordre du backend. */
    assessments: UISchoolAssessment[]
    onClose?: () => void
    /**
     * Pas d'en-tête : la page tient sous celui d'une autre (un onglet de la fiche d'un élève, qui nomme
     * déjà l'élève). La relecture, s'il y en a une, reste au-dessus de la liste. Défaut `false`.
     */
    hideHeader?: boolean
    /** Une relecture est en cours : la flèche de l'en-tête tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    onAddAssessment?: () => void

    // ── Feuille de détail (contrôlée) ──
    /** Le contrôle dont la feuille est ouverte — `null` : aucune. */
    openAssessmentId?: AssessmentId | null
    onOpenAssessment?: (id: AssessmentId) => void
    onCloseAssessment?: () => void
    /** « Modifier » dans la feuille : l'écran referme la feuille et ouvre le formulaire prérempli. */
    onEditAssessment?: (id: AssessmentId) => void
    /** « Supprimer » dans la feuille : l'écran referme la feuille et demande confirmation. */
    onDeleteAssessment?: (id: AssessmentId) => void

    // ── Suppression (contrôlée) ──
    /** Le contrôle dont la suppression attend sa confirmation — `null` : pas de dialogue. */
    deleteConfirmId?: AssessmentId | null
    onConfirmDelete?: () => void
    onCancelDelete?: () => void
    /** La suppression est partie : la ligne s'estompe et ne répond plus. */
    deletingId?: AssessmentId | null
    /** La suppression a échoué : le message s'affiche, la ligne est revenue. */
    deleteError?: string | null
    onDismissDeleteError?: () => void

    /** La feuille d'ajout / de modification. Absente, la page ne la monte pas. */
    assessmentForm?: StudentAssessmentFormProps

    /**
     * Le titre de la page et la phrase de la liste vide, quand ce n'est pas l'élève qui la lit. Le
     * tuteur lit les contrôles d'un élève : « Contrôles de Léa », « Ajoute un contrôle pour Léa »
     * (`SchoolAssessmentsScreen.js`, `isTutorContext`). Absents (défaut) : « Mes contrôles » et la
     * phrase de l'élève.
     */
    title?: string
    emptyMessage?: string

    // ── État de page ──
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function StudentAssessmentsPage(props: StudentAssessmentsPageProps) {
    const {
        assessments,
        onClose,
        hideHeader = false,
        refreshing = false,
        onRefresh,
        onAddAssessment,
        openAssessmentId = null,
        onOpenAssessment,
        onCloseAssessment,
        onEditAssessment,
        onDeleteAssessment,
        deleteConfirmId = null,
        onConfirmDelete,
        onCancelDelete,
        deletingId = null,
        deleteError,
        onDismissDeleteError,
        assessmentForm,
        title = TEXTS.title,
        emptyMessage = TEXTS.emptyMessage,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const openAssessment = assessments.find((item) => item.id === openAssessmentId) ?? null
    const hasDeleteError = deleteError != null && deleteError !== ""
    const isEditing = assessmentForm?.isEditing === true

    const header = <ClosableHeader title={title} onClose={onClose} onRefresh={onRefresh} refreshing={refreshing} />

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={hideHeader ? undefined : header}
            loading={loading}
            skeleton={<StudentAssessmentsSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
        >
            {hideHeader && onRefresh != null && (
                <Row justify="end">
                    <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />
                </Row>
            )}
            {assessments.length === 0 ? (
                <Stack gap="2" align="center">
                    <EmptyState iconName="calendar-check" title={TEXTS.emptyTitle} message={emptyMessage} />
                    <Button label={TEXTS.add} iconName="plus" onPress={onAddAssessment} />
                </Stack>
            ) : (
                <CardList>
                    {assessments.map((assessment) => {
                        const deleting = assessment.id === deletingId
                        return (
                            <SchoolAssessmentRow
                                key={assessment.id}
                                assessment={assessment}
                                eyebrow={
                                    deleting
                                        ? TEXTS.deleting
                                        : preparationStatus(preparationProgress(assessment.chaptersProgress))
                                }
                                dimmed={deleting}
                                onPress={onOpenAssessment != null ? () => onOpenAssessment(assessment.id) : undefined}
                            />
                        )
                    })}
                    <CardListLinkRow label={TEXTS.add} iconName="plus" onPress={onAddAssessment} />
                </CardList>
            )}

            <SchoolAssessmentDetailModal
                visible={openAssessment != null}
                onClose={() => onCloseAssessment?.()}
                assessment={openAssessment}
                title={TEXTS.detailTitle}
                wrapChapterNames
                onEdit={onEditAssessment != null ? () => onEditAssessment(openAssessment!.id) : undefined}
                onDelete={onDeleteAssessment != null ? () => onDeleteAssessment(openAssessment!.id) : undefined}
            />

            <ConfirmDialog
                visible={deleteConfirmId != null}
                title={TEXTS.deleteTitle}
                message={TEXTS.deleteMessage}
                confirmLabel={TEXTS.deleteConfirm}
                onConfirm={() => onConfirmDelete?.()}
                onCancel={() => onCancelDelete?.()}
            />

            <Snackbar
                visible={hasDeleteError}
                message={deleteError ?? ""}
                tone="danger"
                onDismiss={() => onDismissDeleteError?.()}
            />

            {assessmentForm != null && (
                <AddSchoolAssessmentModal
                    {...assessmentForm}
                    voice="student"
                    title={isEditing ? TEXTS.formEditTitle : TEXTS.formAddTitle}
                    submitLabel={isEditing ? TEXTS.formEditSubmit : TEXTS.formAddSubmit}
                    submitIconName={isEditing ? null : "check"}
                    showSubmitHint
                />
            )}
        </PageTemplate>
    )
}
